import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Skeleton, Stack } from '@mui/material';
import { Image } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { get } from 'lodash';
import counselorServices from '../../services/counselorServices';
import { routers } from '../../router/router';
import { setCurrentStudentId } from '../../app/basicInformationSlice/basicInformationSlice';
import { setSidebarCollapsed } from '../../app/appSlice';
import { STRIPE_PRICE_NAME, STRIPE_SUBSCRIPTION_STATUS } from '../../constants/common';
import basicInformationServices from '../../services/basicInformationApi';
import GroupsIcon from '@mui/icons-material/Groups';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { getUserId } from '../../utils/sessionStorage';
import { PlusCircleOutlined } from '@ant-design/icons';
import ConnectChildModal from './ConnectChildModal';
import toast from 'react-hot-toast';

const isDemoStudentEmail = (email?: string) => {
  const e = (email ?? '').trim().toLowerCase();
  return (
    e === 'student-demo@collegeplanningsource.com' ||
    e === 'student-demo@gradmap.com'
  );
};

const getPlanDisplayName = (plan: string | undefined) => {
  if (!plan) return '';
  switch (plan) {
    case STRIPE_PRICE_NAME.GoldMonth:
    case STRIPE_PRICE_NAME.GoldYear:
      return 'Gold Plan';
    case STRIPE_PRICE_NAME.SilverPlusMonth:
    case STRIPE_PRICE_NAME.SilverPlusYear:
      return 'Silver Plus Plan';
    case STRIPE_PRICE_NAME.SilverMonth:
    case STRIPE_PRICE_NAME.SilverYear:
      return 'Silver Plan';
    case STRIPE_PRICE_NAME.GradMapStarter:
      return 'Starter Plan';
    default:
      return plan;
  }
};

const ParentMainDashboard = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const personalStore = useAppSelector((store) => store.basicInformation.myBasicInformation);
  const userId = personalStore?.student_id ?? getUserId();
  const [children, setChildren] = useState<any[]>([]);
  const [selectedChild, setSelectedChild] = useState<any | null>(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState<any>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isLoadingChildren, setIsLoadingChildren] = useState(true);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  const [connectChildModalOpen, setConnectChildModalOpen] = useState(false);

  useEffect(() => {
    if (userId) {
      setIsLoadingChildren(true);
      counselorServices
        .getPartner(userId)
        .then((res: any) => {
          const list = res ?? [];
          setChildren(list);
          if (list.length && !selectedChild) {
            // Prefer a real connected child over the seeded demo account
            const preferred =
              list.find((r: any) => !isDemoStudentEmail(r?.student_info?.email ?? r?.email)) ??
              list[0];
            setSelectedChild(preferred);
          }
        })
        .finally(() => setIsLoadingChildren(false));
    } else {
      setIsLoadingChildren(false);
    }
  }, [userId]);

  useEffect(() => {
    if (selectedChild?.student_id) {
      setIsLoadingSubscription(true);
      // Call API directly — do not write child's Stripe status into global Redux
      // (that incorrectly triggers "subscription canceled" for the parent login).
      basicInformationServices
        .getEnableExtention(selectedChild.student_id)
        .then((info: any) => setSubscriptionInfo(info))
        .catch(() => setSubscriptionInfo(null))
        .finally(() => setIsLoadingSubscription(false));
    } else {
      setSubscriptionInfo(null);
      setIsLoadingSubscription(false);
    }
  }, [selectedChild?.student_id]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const rawPlan = get(subscriptionInfo, 'subcription_plan') || get(subscriptionInfo, 'payload.subcription_plan');
  const rawStatus =
    get(subscriptionInfo, 'subcription_status') ||
    get(subscriptionInfo, 'payload.subcription_status');
  const planName = getPlanDisplayName(rawPlan);
  const isStarterPlan = rawPlan === STRIPE_PRICE_NAME.GradMapStarter;
  const isCanceledPlan = rawStatus === STRIPE_SUBSCRIPTION_STATUS.canceled;
  // Same UX as Starter: parent cannot open full profile until child plan is usable
  const isRestrictedPlan =
    !isDemoStudentEmail(selectedChild?.student_info?.email ?? selectedChild?.email) &&
    (isStarterPlan || isCanceledPlan);

  const cardSx = {
    cursor: 'pointer',
    flex: 1,
    minWidth: isMobile ? '120px' : '180px',
    maxWidth: isMobile ? 'none' : '220px',
    backgroundColor: '#fff',
    borderRadius: '16px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    border: '1px solid #E5E7EB',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: isMobile ? 2 : 3,
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
      border: '1px solid #D6DBE6',
    },
  };

  const iconCircleSx = {
    width: 64,
    height: 64,
    borderRadius: '50%',
    backgroundColor: 'rgba(77, 136, 240, 0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mb: 1.5,
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '1200px', margin: '0 auto', padding: isMobile ? 2 : 3 }}>
      {/* Welcome Banner */}
      <Box
        className="my-3 p-4"
        sx={{
          background: 'linear-gradient(90deg, #4d88f0 0%, #8cb5f3 100%)',
          borderRadius: '24px',
          color: 'white',
          boxShadow: '0 8px 32px rgba(77, 136, 240, 0.2)',
        }}
      >
        <Stack
          direction={isMobile ? 'column' : 'row'}
          alignItems="center"
          spacing={isMobile ? 2 : 4}
          textAlign={isMobile ? 'center' : 'left'}
        >
          <Image
            src="./assets/images/Icon-01.svg"
            roundedCircle
            width={isMobile ? '80px' : '100px'}
          />
          <Stack>
            <Box sx={{ fontSize: isMobile ? 24 : 36, fontWeight: 700 }}>
              Welcome to GradMap! 👋
            </Box>
            <Box sx={{ fontSize: isMobile ? 16 : 18, fontWeight: 400, opacity: 0.95 }}>
              Stay connected to your child's college planning journey.
            </Box>
          </Stack>
        </Stack>
      </Box>

      {/* Children List */}
      <Stack
        direction="row"
        flexWrap="wrap"
        spacing={1.5}
        sx={{ mt: 3, mb: 2 }}
      >
        {isLoadingChildren ? (
          <>
            <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: '12px' }} />
            <Skeleton variant="rectangular" width={140} height={40} sx={{ borderRadius: '12px' }} />
            <Skeleton variant="rectangular" width={100} height={40} sx={{ borderRadius: '12px' }} />
            <Skeleton variant="rectangular" width={110} height={40} sx={{ borderRadius: '12px' }} />
          </>
        ) : (
          <>
        {children.map((child) => {
          const name = [child?.student_info?.first_name, child?.student_info?.last_name]
            .filter(Boolean)
            .join(' ');
          const year = child?.student_info?.year_finish_high_school ?? '';
          const isSelected = selectedChild?.student_id === child?.student_id;
          return (
            <Box
              key={child.student_id}
              onClick={() => setSelectedChild(child)}
              sx={{
                px: 2,
                py: 1.25,
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: 15,
                fontWeight: 500,
                backgroundColor: isSelected ? '#4d88f0' : '#E5E7EB',
                color: isSelected ? 'white' : '#374151',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: isSelected ? '#3d78e0' : '#D1D5DB',
                },
              }}
            >
              {name} {year}
            </Box>
          );
        })}
        <Box
          onClick={() => setConnectChildModalOpen(true)}
          sx={{
            px: 2,
            py: 1.25,
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: 15,
            fontWeight: 500,
            backgroundColor: '#E5E7EB',
            color: '#374151',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            transition: 'all 0.2s',
            '&:hover': {
              backgroundColor: '#D1D5DB',
            },
          }}
        >
          <PlusCircleOutlined size={18}/> Add Child
        </Box>
          </>
        )}
      </Stack>

      {/* Child Summary Card */}
      {isLoadingChildren ? (
        <Box
          sx={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            border: '1px solid #D6DBE6',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            p: isMobile ? 2 : 3,
            mb: 3,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Skeleton variant="circular" width={56} height={56} />
            <Stack sx={{ flex: 1 }}>
              <Skeleton variant="text" width={180} height={28} />
              <Skeleton variant="text" width={120} height={24} sx={{ mt: 1 }} />
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: '8px' }} />
                <Skeleton variant="text" width={80} height={24} />
              </Stack>
            </Stack>
          </Stack>
        </Box>
      ) : selectedChild ? (
        <Box
          sx={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            border: '1px solid #D6DBE6',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            p: isMobile ? 2 : 3,
            mb: 3,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: '#E5E7EB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                fontWeight: 600,
                color: '#4d88f0',
                flexShrink: 0,
              }}
            >
              {[selectedChild?.student_info?.first_name?.[0], selectedChild?.student_info?.last_name?.[0]]
                .filter(Boolean)
                .join('')
                .toUpperCase() || '?'}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ fontSize: 20, fontWeight: 600, color: '#212529' }}>
                {[selectedChild?.student_info?.first_name, selectedChild?.student_info?.last_name]
                  .filter(Boolean)
                  .join(' ')}
              </Box>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                <Box sx={{ fontSize: 14, color: '#6c757d' }}>
                  Class of {selectedChild?.student_info?.year_finish_high_school ?? '—'}
                </Box>
                {isLoadingSubscription ? (
                  <Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: '8px' }} />
                ) : planName ? (
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.25,
                      borderRadius: '8px',
                      backgroundColor: isStarterPlan ? '#E5E7EB' : '#FEF3C7',
                      fontSize: 13,
                      fontWeight: 500,
                      color: isStarterPlan ? '#6B7280' : '#92400E',
                    }}
                  >
                    {planName}{isStarterPlan ? ' Free' : ''}
                  </Box>
                ) : null}
              </Stack>
            </Box>
          </Stack>
          <Stack direction="column" spacing={2} sx={{ mt: 2 }}>
            <Box sx={{ fontSize: 14, color: '#6c757d' }}>
              {isCanceledPlan
                ? "This student's subscription is canceled — upgrade to restore parent access"
                : isStarterPlan
                ? 'Free plan — upgrade to Silver or Gold to unlock full parent access'
                : 'You are paying for this plan'}
            </Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={2}
            >
              <Stack direction="row" spacing={1.5} flexWrap="wrap">
                <Box
                  component="button"
                  onClick={() => {
                    if (isRestrictedPlan) return;
                    dispatch(setCurrentStudentId(selectedChild.student_id));
                    dispatch(setSidebarCollapsed(false));
                    history.push((routers.viewStudent?.path ?? '/application/counselor/:id').replace(':id', String(selectedChild.student_id)));
                  }}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: isRestrictedPlan ? '#9CA3AF' : '#4d88f0',
                    color: 'white',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: isRestrictedPlan ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit',
                    opacity: isRestrictedPlan ? 0.7 : 1,
                    '&:hover': { backgroundColor: isRestrictedPlan ? '#9CA3AF' : '#3d78e0' },
                  }}
                >
                  View Full Profile
                </Box>
                {isRestrictedPlan && (
                  <Box
                    component="button"
                    onClick={() =>
                      history.push(
                        (routers.parentUpgrade?.path ?? '/dashboard/upgrade') +
                          '?studentId=' +
                          (selectedChild?.student_id ?? '')
                      )
                    }
                    sx={{
                      px: 2.5,
                      py: 1,
                      borderRadius: '8px',
                      border: '1px solid #D1D5DB',
                      backgroundColor: '#F8F9FA',
                      color: '#333C4E',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      '&:hover': {
                        backgroundColor: '#4d88f0',
                        // borderColor: '#ADB5BD',
                        color: 'white',
                      },
                    }}
                  >
                    Upgrade Plan
                  </Box>
                )}
              </Stack>
              <Box
                component="span"
                onClick={() =>
                  history.push(
                    (routers.parentUpgrade?.path ?? '/dashboard/upgrade') +
                      '?studentId=' +
                      (selectedChild?.student_id ?? '')
                  )
                }
                sx={{
                  fontSize: 14,
                  color: '#4d88f0',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  '&:hover': { color: '#3d78e0' },
                }}
              >
                Manage Plan
              </Box>
            </Stack>
          </Stack>
        </Box>
      ) : null}

      {/* 3 Action Cards */}
      <Box
        sx={{
          py: isMobile ? 2 : 4,
          px: isMobile ? 0 : 2,
          mt: 2,
        }}
      >
        <Stack
          direction="row"
          flexWrap="wrap"
          justifyContent="center"
          gap={isMobile ? 1.5 : 3}
          sx={{ width: '100%' }}
        >
          <Box
            sx={cardSx}
            onClick={() => {
              const studentId = selectedChild?.student_id ?? children[0]?.student_id;
              if (!studentId) {
                toast.error('Add a student first');
                return;
              }
              dispatch(setCurrentStudentId(studentId));
              dispatch(setSidebarCollapsed(false));
              history.push(routers.viewStudent.path.replace(':id', String(studentId)));
            }}
          >
            <Box sx={iconCircleSx}>
              <GroupsIcon sx={{ fontSize: 32, color: '#4d88f0' }} />
            </Box>
            <Box sx={{ fontSize: 15, fontWeight: 600, color: '#212529', textAlign: 'center' }}>
              View Students
            </Box>
          </Box>
          <Box
            sx={cardSx}
            onClick={() => window.open('https://docs.gradmap.com/en', '_blank')}
          >
            <Box sx={iconCircleSx}>
              <MenuBookRoundedIcon sx={{ fontSize: 32, color: '#4d88f0' }} />
            </Box>
            <Box sx={{ fontSize: 15, fontWeight: 600, color: '#212529', textAlign: 'center' }}>
              Resources
            </Box>
          </Box>
          <Box
            sx={cardSx}
            onClick={() => history.push(routers.settings.path + '?tab=3')}
          >
            <Box sx={iconCircleSx}>
              <SettingsRoundedIcon sx={{ fontSize: 32, color: '#4d88f0' }} />
            </Box>
            <Box sx={{ fontSize: 15, fontWeight: 600, color: '#212529', textAlign: 'center' }}>
              Settings
            </Box>
          </Box>
        </Stack>
      </Box>

      <ConnectChildModal
        open={connectChildModalOpen}
        onClose={() => setConnectChildModalOpen(false)}
        onSuccess={() => {
          counselorServices.getPartner(userId).then((res: any) => {
            setChildren(res ?? []);
            if (res?.length && !selectedChild) {
              setSelectedChild(res[0]);
            }
          });
        }}
      />
    </Box>
  );
};

export default ParentMainDashboard;
